import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { format, parse, differenceInYears } from "date-fns";
import { fr } from "date-fns/locale";
import CalendarModal from "../CalendarModal/CalendarModal";
import "../DatePickerComponent/index.less";

type SelectedDate = {
  day?: number;
  month: number;
  year: number;
};

interface BirthDatePickerProps {
  value: string | null;
  onChange: (date: string | null) => void;
  className?: string;
  onModalStateChange?: (isOpen: boolean) => void;
}

const BirthDatePicker: React.FC<BirthDatePickerProps> = ({ value, onChange, className, onModalStateChange }) => {
  const { t } = useTranslation();
  const [showCalendarModal, setShowCalendarModal] = useState(false);

  const formatDisplayDate = (dateString: string) => {
    if (!dateString) return "";
    try {
      let parsedDate: Date;

      parsedDate = parse(dateString, "MM/dd/yyyy", new Date());
      if (!isNaN(parsedDate.getTime())) {
        return format(parsedDate, "dd/MM/yyyy", { locale: fr });
      }

      const cleanedDate = dateString.includes("T") ? dateString.split("T")[0] : dateString;
      parsedDate = parse(cleanedDate, "yyyy-MM-dd", new Date());
      if (!isNaN(parsedDate.getTime())) {
        return format(parsedDate, "dd/MM/yyyy", { locale: fr });
      }

      parsedDate = new Date(dateString);
      if (!isNaN(parsedDate.getTime())) {
        return format(parsedDate, "dd/MM/yyyy", { locale: fr });
      }

      console.error("Invalid date format:", dateString);
      return "";
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  const handleDateSelect = (date: SelectedDate) => {
    if (date.day === undefined) {
      console.error("Day is required but was undefined");
      return;
    }

    const day = date.day.toString().padStart(2, "0");
    const month = (date.month + 1).toString().padStart(2, "0");
    const year = date.year.toString();

    if (parseInt(day) < 1 || parseInt(day) > 31 || parseInt(month) < 1 || parseInt(month) > 12 || year.length !== 4) {
      console.error("Invalid date components:", { day, month, year });
      return;
    }

    const birthDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const today = new Date();
    const age = differenceInYears(today, birthDate);

    if (age < 18) {
      message.error(t("minimum-age-required"));
      return;
    }

    const backendDate = `${month}/${day}/${year}`;
    onChange(backendDate);
    setShowCalendarModal(false);
  };

  const handleOpenModal = () => {
    setShowCalendarModal(true);
    onModalStateChange?.(true);
  };

  const handleCloseModal = () => {
    setShowCalendarModal(false);
    onModalStateChange?.(false);
  };

  const handleDateSelectWrapper = (date: SelectedDate) => {
    handleDateSelect(date);
    onModalStateChange?.(false);
  };

  return (
    <>
      <div className="date-picker-wrapper" onClick={handleOpenModal}>
        {!value && <span className="date-picker-placeholder">{t("date-of-birth-label")}</span>}
        <input type="text" value={value ? formatDisplayDate(value) : ""} className={`datePicker ${className || ""}`} readOnly />
      </div>
      {showCalendarModal && (
        <CalendarModal title={t("select-birth-date") as string} onClose={handleCloseModal} onDateSelect={handleDateSelectWrapper} minAge={18} />
      )}
    </>
  );
};

export default BirthDatePicker;
